import { leadCategoryIdentity, leadCategoryKey, leadCategoryLabel } from "../utils/leadCategories";
import { adjustedCategory, isProductCategory } from "../config/productCategories";
import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search, AlertCircle, X, Copy, ChevronDown, Check, Heart, ImageIcon, ExternalLink, Loader2, Box, Plus, RefreshCw, Trash2, ArrowUp, ArrowDown, Settings2, Pencil, LayoutGrid, ArrowUpDown, MoreHorizontal, Share2, Sparkles, Layers } from "lucide-react";
import { toast } from "react-toastify";
import { getLeads, updateLead, deleteLead, deleteLeadCategory, reorderLeadCategories, updateLeadCategoryLabel, findLeadOptions, type Lead, type LeadsResponse, type UpdateLeadRequest } from "../services/LeadsService";
import { getAffiliateLink } from "../services/ProductsService";
import {
  searchProduct,
  searchHomeDepotProduct,
  type StoreProductResult,
  type HomeDepotStoreProductResult,
} from "../services/ProductSearchService";
import { useRetailer } from "../hooks/useRetailer";
import { useTranslation } from "../context/LanguageContext";
import { getProductImageUrl } from "../utils/imageUtils";
import SetAlertButton from "../components/buttons/SetAlertButton";
import CheckStockButton from "../components/buttons/CheckStockButton";
import LeadSaveButton from "../components/buttons/LeadSaveButton";
import { useLeadFavoriteIDs } from "../hooks/useLeadsFavorites";
import { makeLeadFavoriteKey } from "../services/LeadsFavoritesService";
import { useUserContext } from "../context/UserContext";
import { isFreeTier } from "../utils/userUtils";
import { UpgradeBanner } from "../components/UpgradeModal";
import NearbyResults from "../components/NearbyResults";
import ZipCodeModal from "../components/ZipCodeModal";
import TopLeadsView from "../components/TopLeadsView";
import LayoutModeMenu from "../components/LayoutModeMenu";
import { LAYOUT_MODE_KEYS, setLayoutMode, useLayoutMode } from "../utils/layoutModePreference";
import DiscordLeadImportModal from "../components/DiscordLeadImportModal";
import { supportsDiscordImport } from "../services/DiscordLeadImportService";
import "./LeadsPage.css";
import {
  buildUpdateLeadRequest,
  filterLeads,
  formatPrice,
  formatRelativeTime,
  getAvailableCategories,
  getCategoryCounts,
  getLeadDiscountPercent,
  getLeadExternalLink,
  getLeadOriginalPrice,
  getLeadStockSearchSku,
  resolveWalmartIds,
  type LeadEditFormState,
} from "./LeadsPage.utils";

const LEADS_VIEW_MODE_KEY = "dealhawk_leads_view_mode";
const LEADS_EXPLAINER_DISMISSED_KEY = "dealhawk_leads_explainer_dismissed";
// Older desktop-only layout choice; carried into the per-tab preference once.
const LEADS_DESKTOP_LAYOUT_KEY = "dealhawk_leads_desktop_layout";
const migrateDesktopLayout = () => {
  try {
    if (window.localStorage.getItem(LEADS_DESKTOP_LAYOUT_KEY) === "grid" && !window.localStorage.getItem(LAYOUT_MODE_KEYS.leads)) {
      setLayoutMode("leads", "grid");
    }
    window.localStorage.removeItem(LEADS_DESKTOP_LAYOUT_KEY);
  } catch {
    // Storage unavailable: the default applies.
  }
};
const TOP_LEADS_WINDOW_HOURS = 24;
type LeadSort = "newest" | "discount";
const CATEGORY_SEARCH_THRESHOLD = 8;

const getLeadIdLabel = (retailer: string): string =>
  retailer === "home_depot"
    ? "ItemId"
    : retailer === "target"
      ? "TCIN"
      : retailer === "bestbuy" || retailer === "costco" || retailer === "lowes" || retailer === "tractor_supply" || retailer === "northern_tool" || retailer === "samsclub" || retailer === "ace_hardware"
        ? "Product ID"
        : "UPC";
type LeadsViewMode = "top" | "all";

const readPersistedViewMode = (): LeadsViewMode => {
  if (typeof window === "undefined") return "top";
  try {
    const raw = window.localStorage.getItem(LEADS_VIEW_MODE_KEY);
    return raw === "all" ? "all" : "top";
  } catch {
    return "top";
  }
};

const readLeadsExplainerVisibility = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(LEADS_EXPLAINER_DISMISSED_KEY) !== "true";
  } catch {
    return true;
  }
};

const formatToggleCount = (count: number) => {
  if (count < 1000) return String(count);
  if (count < 10000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(count / 1000)}k`;
};

type LeadListHeaderRow = { type: "header"; key: string; count: number };
type LeadListRow = LeadListHeaderRow | { type: "leads"; leads: Lead[] };

// Phones get one card per row; wider screens show table-style rows, or a photo grid if the user picks it.
const LEAD_GRID_MIN_WIDTH = 768;
const LEAD_TILE_MIN_WIDTH = 220;
const getLeadColumns = (width: number) =>
  width < LEAD_GRID_MIN_WIDTH ? 1 : Math.min(6, Math.max(2, Math.floor((width - 32 + 14) / (LEAD_TILE_MIN_WIDTH + 14))));

const getLeadVariantOptions = (lead: Lead, retailer: string) => {
  const resolved = resolveWalmartIds(lead, retailer);
  return (lead.variantCriteria || []).filter((option) => option?.sku && option.sku !== resolved.sku && option.sku !== resolved.upc);
};

interface LeadsViewToggleProps {
  value: LeadsViewMode;
  onChange: (tab: LeadsViewMode) => void;
  topCount: number;
  allCount: number;
}

const LeadsViewToggle: React.FC<LeadsViewToggleProps> = ({ value, onChange, topCount, allCount }) => {
  const { t } = useTranslation();
  // Top / All only (matches Pinto); saved leads are a heart toggle in the All filters row.
  const tabs: { id: LeadsViewMode; label: string; shortLabel: string; count: number }[] = [
    { id: "top", label: t("leads.topLeads"), shortLabel: t("leads.topLeadsShort"), count: topCount },
    { id: "all", label: t("leads.allLeads"), shortLabel: t("leads.allLeadsShort"), count: allCount },
  ];
  return (
    <div className="leads-view-toggle" role="tablist" aria-label={t("leads.leadsView")}>
      {tabs.map(({ id, label, shortLabel, count }) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={label}
            onClick={() => onChange(id)}
            className={`leads-view-option${active ? " is-active" : ""}`}
          >
            <span>{shortLabel}</span>
            {count > 0 && <span className="leads-view-count">{formatToggleCount(count)}</span>}
          </button>
        );
      })}
    </div>
  );
};

interface ManageLeadCategoriesModalProps {
  categories: string[];
  categoryLabels: Record<string, string>;
  deletingCategory: string | null;
  editingCategory: string | null;
  saving: boolean;
  onClose: () => void;
  onCreate: () => void;
  onDelete: (category: string) => void;
  onEdit: (category: string) => void;
  onSave: (categories: string[]) => void;
}

const ManageLeadCategoriesModal: React.FC<ManageLeadCategoriesModalProps> = ({
  categories,
  categoryLabels,
  deletingCategory,
  editingCategory,
  saving,
  onClose,
  onCreate,
  onDelete,
  onEdit,
  onSave,
}) => {
  const { t } = useTranslation();
  const [orderedCategories, setOrderedCategories] = useState(categories);

  useEffect(() => {
    setOrderedCategories(categories);
  }, [categories]);

  const move = (index: number, direction: -1 | 1) => {
    setOrderedCategories((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) return previous;
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const dirty = orderedCategories.some((category, index) => category !== categories[index]);
  const busy = saving || deletingCategory !== null || editingCategory !== null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={() => { if (!busy) onClose(); }}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-lead-categories-title"
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-dark-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-100 p-4 dark:border-dark-700">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400">{t("leads.admin")}</p>
            <h2 id="manage-lead-categories-title" className="mt-0.5 text-base font-semibold text-gray-900 dark:text-white">{t("leads.manageCategories")}</h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">{t("leads.manageCategoriesHint")}</p>
          </div>
          <button type="button" aria-label={t("leads.closeCategoryManager")} disabled={busy} onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-dark-700 dark:hover:text-white"><X size={18} /></button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-3">
          {orderedCategories.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center dark:border-dark-600">
              <p className="text-sm font-medium text-gray-700 dark:text-dark-200">{t("leads.noCustomCategories")}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">{t("leads.noCustomCategoriesHint")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {orderedCategories.map((category, index) => {
                const categoryKey = leadCategoryKey(category);
                const displayLabel = leadCategoryLabel(categoryKey, categoryLabels);
                return (
                <div key={category} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 dark:border-dark-600 dark:bg-dark-900">
                  <span className="w-6 flex-shrink-0 text-center text-[11px] font-semibold text-gray-400">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-dark-100">{displayLabel}</span>
                  <button type="button" aria-label={t("leads.editLabelNamed", { name: displayLabel })} title={t("leads.editLabel")} disabled={busy} onClick={() => onEdit(categoryKey)} className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-violet-600 disabled:opacity-25 dark:hover:bg-dark-700 dark:hover:text-violet-400">
                    {editingCategory === categoryKey ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />}
                  </button>
                  <button type="button" aria-label={t("leads.moveUpNamed", { name: displayLabel })} title={t("leads.moveUp")} disabled={busy || index === 0} onClick={() => move(index, -1)} className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-violet-600 disabled:opacity-25 dark:hover:bg-dark-700 dark:hover:text-violet-400"><ArrowUp size={15} /></button>
                  <button type="button" aria-label={t("leads.moveDownNamed", { name: displayLabel })} title={t("leads.moveDown")} disabled={busy || index === orderedCategories.length - 1} onClick={() => move(index, 1)} className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-violet-600 disabled:opacity-25 dark:hover:bg-dark-700 dark:hover:text-violet-400"><ArrowDown size={15} /></button>
                  <button type="button" aria-label={t("leads.deleteCategoryNamed", { name: displayLabel })} title={t("leads.deleteCategory")} disabled={busy} onClick={() => onDelete(category)} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30 dark:hover:text-red-400">
                    {deletingCategory === categoryKey ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 p-3 dark:border-dark-700">
          <button type="button" disabled={busy} onClick={onCreate} className="mr-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50 dark:text-violet-300 dark:hover:bg-violet-950/30"><Plus size={14} />{t("leads.newCategory")}</button>
          <button type="button" disabled={busy} onClick={onClose} className="rounded-lg px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-dark-300 dark:hover:bg-dark-700">{t("common.cancel")}</button>
          <button type="button" disabled={busy || !dirty} onClick={() => onSave(orderedCategories)} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
            {saving && <Loader2 size={14} className="animate-spin" />}{t("leads.saveOrder")}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
};

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const currentRetailer = useRetailer();
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const isAdmin = user?.admin === true;
  const isFreeUser = isFreeTier(user);
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [sortBy, setSortBy] = useState<LeadSort>("newest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [moreLead, setMoreLead] = useState<Lead | null>(null);
  const [moreLinkLoading, setMoreLinkLoading] = useState(false);
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  const revealScrollTopRef = useRef(0);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [viewMode, setViewMode] = useState<LeadsViewMode>(() => readPersistedViewMode());
  const [showLeadsExplainer, setShowLeadsExplainer] = useState(readLeadsExplainerVisibility);

  useEffect(() => {
    try {
      window.localStorage.setItem(LEADS_VIEW_MODE_KEY, viewMode);
    } catch {
      // ignore storage errors
    }
  }, [viewMode]);

  const handleViewModeChange = useCallback((mode: LeadsViewMode) => {
    setViewMode(mode);
    if (mode === "top") {
      setSearchQuery("");
      setIsSearchOpen(false);
      setSelectedCategories([]);
      setShowSavedOnly(false);
    }
  }, []);

  const handleTabChange = useCallback((tab: LeadsViewMode) => {
    setShowSavedOnly(false);
    handleViewModeChange(tab);
  }, [handleViewModeChange]);

  const dismissLeadsExplainer = useCallback(() => {
    setShowLeadsExplainer(false);
    try {
      window.localStorage.setItem(LEADS_EXPLAINER_DISMISSED_KEY, "true");
    } catch {
      // The banner can still be dismissed for this session when storage is unavailable.
    }
  }, []);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [savingCategoryOrder, setSavingCategoryOrder] = useState(false);

  useEffect(() => {
    setSelectedCategories([]);
    setCategorySearch("");
    setSearchQuery("");
    setIsSearchOpen(false);
    setSortBy("newest");
    setShowSortMenu(false);
    setMoreLead(null);
    setShowSavedOnly(false);
    setShowImport(false);
    setShowCategoryManager(false);
  }, [currentRetailer]);
  const parentRef = useRef<HTMLDivElement>(null);
  const [listWidth, setListWidth] = useState(0);
  useState(migrateDesktopLayout);
  // List or grid, remembered per tab: All defaults to list, Top to grid. Phones get two columns.
  const [leadsLayout, setLeadsLayout] = useLayoutMode("leads");
  const [leadsTopLayout, setLeadsTopLayout] = useLayoutMode("leadsTop");
  const isWideList = listWidth >= LEAD_GRID_MIN_WIDTH;
  const leadColumns = leadsLayout === "grid" ? (isWideList ? getLeadColumns(listWidth) : 2) : 1;
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [findingOptions, setFindingOptions] = useState<Record<string, boolean>>({});
  const [isSearchingNearby, setIsSearchingNearby] = useState(false);
  const [showNearbyResultsModal, setShowNearbyResultsModal] = useState(false);
  const [nearbySearchResults, setNearbySearchResults] = useState<StoreProductResult[]>([]);
  const [nearbyHomeDepotResults, setNearbyHomeDepotResults] = useState<HomeDepotStoreProductResult[]>([]);
  const [nearbySearchError, setNearbySearchError] = useState<string | null>(null);
  const [nearbyAmazonData, setNearbyAmazonData] = useState<{
    price?: number; title?: string; url?: string; isPrime?: boolean;
  } | null>(null);
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [pendingNearbySearch, setPendingNearbySearch] = useState<{
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  } | null>(null);

  // Get saved lead IDs for filtering
  const { data: savedLeadIds } = useLeadFavoriteIDs(currentRetailer);

  const { data: leadsData, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["leads", currentRetailer],
    queryFn: () => getLeads(currentRetailer),
    refetchOnWindowFocus: false,
  });

  const handleEditLead = useCallback((lead: Lead) => setEditingLead(lead), []);
  const handleDeleteLead = useCallback(async (lead: Lead) => {
    if (!confirm(t("leads.confirmDeleteLead", { title: lead.title }))) return;
    try {
      await deleteLead(lead.id, currentRetailer);
      queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
      toast.success(t("products.leadDeleted"));
    } catch { toast.error(t("products.failedToDeleteLead")); }
  }, [currentRetailer, queryClient, t]);
  const handleSaveEdit = useCallback(async (data: UpdateLeadRequest) => {
    if (!editingLead) return;
    try {
      await updateLead(editingLead.id, data, currentRetailer);
      queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
      setEditingLead(null);
      toast.success(t("products.leadUpdated"));
    } catch { toast.error(t("products.failedToUpdateLead")); }
  }, [editingLead, currentRetailer, queryClient, t]);

  const handleFindOptions = useCallback(async (lead: Lead) => {
    if (currentRetailer !== "walmart" && currentRetailer !== "walmart_online") {
      toast.error(t("leads.optionsWalmartOnly"));
      return;
    }
    setFindingOptions((prev) => ({ ...prev, [lead.id]: true }));
    try {
      const response = await findLeadOptions(lead.id, currentRetailer);
      queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
      toast.success(response.options.length > 0 ? t("leads.foundOptions", { count: response.options.length }) : t("leads.noOptionsFound"));
    } catch (error) {
      const message = error instanceof Error ? error.message : t("leads.failedFindOptions");
      toast.error(message);
    } finally {
      setFindingOptions((prev) => {
        const next = { ...prev };
        delete next[lead.id];
        return next;
      });
    }
  }, [currentRetailer, queryClient, t]);

  const skipAllListWork = viewMode === "top";

  const availableCategories = useMemo(
    () => getAvailableCategories(leadsData?.data, leadsData?.manualCategories, leadsData?.hiddenCategories),
    [leadsData]
  );

  const categoryCounts = useMemo(
    () => (skipAllListWork ? new Map<string, number>() : getCategoryCounts(leadsData?.data)),
    [leadsData?.data, skipAllListWork]
  );

  const manualCategoryKeys = useMemo(
    () => new Set((leadsData?.manualCategories ?? []).map(leadCategoryKey)),
    [leadsData?.manualCategories]
  );

  const displayCategoryLabel = useCallback(
    (categoryKey: string) => leadCategoryLabel(categoryKey, leadsData?.categoryLabels),
    [leadsData?.categoryLabels]
  );

  const handleDeleteCategory = useCallback(async (categoryKey: string) => {
    const isManualCategory = manualCategoryKeys.has(categoryKey);
    if (!isAdmin || (!isManualCategory && !isProductCategory(categoryKey))) return;
    const categoryLabel = displayCategoryLabel(categoryKey);
    const categoryIdentity = leadCategoryIdentity(categoryKey);
    if (!confirm(t("leads.confirmDeleteCategory", { name: categoryLabel }))) return;

    setDeletingCategory(categoryKey);
    try {
      await deleteLeadCategory(
        currentRetailer,
        categoryIdentity,
        isManualCategory ? "manual" : "master"
      );
      queryClient.setQueryData<LeadsResponse>(["leads", currentRetailer], (previous) =>
        previous
          ? {
              ...previous,
              ...(isManualCategory
                ? {
                    manualCategories: (previous.manualCategories ?? []).filter((name) => name !== categoryIdentity),
                    ...(previous.categoryLabels
                      ? { categoryLabels: Object.fromEntries(Object.entries(previous.categoryLabels).filter(([key]) => key !== categoryKey)) }
                      : {}),
                  }
                : { hiddenCategories: [...new Set([...(previous.hiddenCategories ?? []), categoryKey])] }),
            }
          : previous
      );
      setSelectedCategories((previous) => previous.filter((key) => key !== categoryKey));
      toast.success(t("leads.categoryDeleted", { name: categoryLabel }));
      void queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("leads.failedDeleteCategory"));
    } finally {
      setDeletingCategory(null);
    }
  }, [currentRetailer, displayCategoryLabel, isAdmin, manualCategoryKeys, queryClient, t]);

  const handleEditCategoryLabel = useCallback(async (categoryKey: string) => {
    const isManualCategory = manualCategoryKeys.has(categoryKey);
    if (!isAdmin || (!isManualCategory && !isProductCategory(categoryKey))) return;
    const currentLabel = displayCategoryLabel(categoryKey);
    const nextLabel = window.prompt(t("leads.editCategoryLabelPrompt"), currentLabel)?.trim();
    if (!nextLabel || nextLabel === currentLabel) return;

    setEditingCategory(categoryKey);
    try {
      const response = await updateLeadCategoryLabel(
        currentRetailer,
        leadCategoryIdentity(categoryKey),
        isManualCategory ? "manual" : "master",
        nextLabel
      );
      queryClient.setQueryData<LeadsResponse>(["leads", currentRetailer], (previous) =>
        previous ? { ...previous, categoryLabels: { ...(previous.categoryLabels ?? {}), [response.categoryKey]: response.label } } : previous
      );
      toast.success(t("leads.categoryLabelChanged", { name: response.label }));
      void queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("leads.failedUpdateCategoryLabel"));
    } finally {
      setEditingCategory(null);
    }
  }, [currentRetailer, displayCategoryLabel, isAdmin, manualCategoryKeys, queryClient, t]);

  const handleSaveCategoryOrder = useCallback(async (categories: string[]) => {
    if (!isAdmin) return;
    setSavingCategoryOrder(true);
    try {
      const response = await reorderLeadCategories(currentRetailer, categories);
      queryClient.setQueryData<LeadsResponse>(["leads", currentRetailer], (previous) =>
        previous ? { ...previous, manualCategories: response.categories } : previous
      );
      setShowCategoryManager(false);
      toast.success(t("leads.categoryOrderSaved"));
      void queryClient.invalidateQueries({ queryKey: ["leads", currentRetailer] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("leads.failedSaveCategoryOrder"));
    } finally {
      setSavingCategoryOrder(false);
    }
  }, [currentRetailer, isAdmin, queryClient, t]);

  const matchedLeads = useMemo(
    () =>
      skipAllListWork
        ? []
        : filterLeads({
            leads: leadsData?.data,
            searchQuery,
            selectedCategories,
            showSavedOnly: isFreeUser ? false : showSavedOnly,
            savedLeadIds,
            retailer: currentRetailer,
          }),
    [leadsData?.data, searchQuery, selectedCategories, showSavedOnly, savedLeadIds, currentRetailer, isFreeUser, skipAllListWork]
  );

  const filteredLeads = useMemo(() => {
    const sorted = [...matchedLeads];
    if (sortBy === "discount") {
      sorted.sort((a, b) => {
        const discountA = getLeadDiscountPercent(getLeadOriginalPrice(a.msrp, a.onlinePrice), a.lowestPrice);
        const discountB = getLeadDiscountPercent(getLeadOriginalPrice(b.msrp, b.onlinePrice), b.lowestPrice);
        return discountB - discountA || b.lastUpdated - a.lastUpdated;
      });
    } else {
      sorted.sort((a, b) => b.lastUpdated - a.lastUpdated);
    }
    return sorted;
  }, [matchedLeads, sortBy]);

  useEffect(() => {
    parentRef.current?.scrollTo?.({ top: 0 });
    setToolbarHidden(false);
    lastScrollTopRef.current = 0;
    revealScrollTopRef.current = 0;
  }, [searchQuery, selectedCategories, showSavedOnly, sortBy, currentRetailer, viewMode]);

  // Mobile only: slide the toolbar away while scrolling down, bring it back on any scroll up (mirrors the app).
  const handleListScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (window.innerWidth >= 768) {
      if (toolbarHidden) setToolbarHidden(false);
      return;
    }
    const offsetY = event.currentTarget.scrollTop;
    const height = toolbarRef.current?.offsetHeight ?? 0;
    if (height && height !== toolbarHeight) setToolbarHeight(height);
    if (offsetY <= 24) {
      if (toolbarHidden) setToolbarHidden(false);
      lastScrollTopRef.current = offsetY;
      revealScrollTopRef.current = offsetY;
      return;
    }
    const delta = offsetY - lastScrollTopRef.current;
    if (Math.abs(delta) < 10) return;
    lastScrollTopRef.current = offsetY;
    if (delta > 0) {
      if (!toolbarHidden && offsetY > Math.max(120, height * 0.55) && offsetY - revealScrollTopRef.current > 120) {
        setToolbarHidden(true);
      }
    } else {
      revealScrollTopRef.current = offsetY;
      if (toolbarHidden) setToolbarHidden(false);
    }
  }, [toolbarHeight, toolbarHidden]);

  const toggleCategory = useCallback((categoryKey: string) => {
    setSelectedCategories((previous) =>
      previous.includes(categoryKey) ? previous.filter((key) => key !== categoryKey) : [...previous, categoryKey]
    );
  }, []);

  const categoryPillLabel = selectedCategories.length === 0
    ? t("leads.allCategories")
    : selectedCategories.length === 1
      ? displayCategoryLabel(selectedCategories[0])
      : t("leads.categoriesCount", { count: selectedCategories.length });
  const visibleCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return availableCategories;
    return availableCategories.filter((cat) => displayCategoryLabel(cat).toLowerCase().includes(query));
  }, [availableCategories, categorySearch, displayCategoryLabel]);

  const totalLeadsCount = leadsData?.data?.length ?? 0;
  const topLeadsCount = useMemo(() => {
    const data = leadsData?.data;
    if (!data?.length) return 0;
    const cutoff = Date.now() - TOP_LEADS_WINDOW_HOURS * 3600 * 1000;
    let count = 0;
    for (const lead of data) {
      if ((lead.lastUpdated || 0) <= cutoff) continue;
      const orig = Math.max(lead.msrp || 0, lead.onlinePrice || 0);
      if (lead.lowestPrice > 0 && orig > lead.lowestPrice) count++;
    }
    return count;
  }, [leadsData?.data]);

  // On phones, Newest groups by when the lead was found and Biggest savings by discount tier. Both follow the sort order, so groups stay contiguous.
  const listRows = useMemo<LeadListRow[]>(() => {
    const rows: LeadListRow[] = [];
    let currentKey = "";
    let headerIndex = -1;
    const now = Date.now();
    const startOfToday = new Date(now).setHours(0, 0, 0, 0);
    const groupFor = (lead: Lead): string => {
      if (sortBy === "discount") {
        const discount = getLeadDiscountPercent(getLeadOriginalPrice(lead.msrp, lead.onlinePrice), lead.lowestPrice);
        return discount >= 80 ? "tierHuge" : discount >= 50 ? "tierBig" : discount > 0 ? "tierSmall" : "tierNone";
      }
      const updated = lead.lastUpdated || 0;
      if (now - updated < 3600 * 1000) return "groupLastHour";
      if (updated >= startOfToday) return "groupToday";
      if (updated >= startOfToday - 86400 * 1000) return "groupYesterday";
      if (updated >= startOfToday - 6 * 86400 * 1000) return "groupThisWeek";
      return "groupOlder";
    };
    // The desktop grid is one continuous wall of tiles; only the phone list is split into groups.
    const grouped = !isWideList;
    for (const lead of filteredLeads) {
      const key = grouped ? groupFor(lead) : "";
      if (grouped && key !== currentKey) {
        currentKey = key;
        headerIndex = rows.length;
        rows.push({ type: "header", key, count: 0 });
      }
      if (grouped) (rows[headerIndex] as LeadListHeaderRow).count++;
      const last = rows[rows.length - 1];
      if (last?.type === "leads" && last.leads.length < leadColumns) last.leads.push(lead);
      else rows.push({ type: "leads", leads: [lead] });
    }
    return rows;
  }, [filteredLeads, sortBy, leadColumns, isWideList]);

  const savedLeadKeys = useMemo(() => new Set(savedLeadIds ?? []), [savedLeadIds]);
  const savedCountForRetailer = useMemo(() => {
    if (!savedLeadKeys.size) return 0;
    let count = 0;
    for (const lead of leadsData?.data ?? []) {
      if (savedLeadKeys.has(makeLeadFavoriteKey(currentRetailer, lead.id))) count++;
    }
    return count;
  }, [savedLeadKeys, leadsData?.data, currentRetailer]);

  const hasListElement = !isLoading && filteredLeads.length > 0;
  useEffect(() => {
    const element = parentRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const update = () => setListWidth(element.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasListElement]);

  const virtualizer = useVirtualizer({
    count: listRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (listRows[index]?.type === "header" ? 44 : leadColumns > 1 ? 380 : isWideList ? 97 : 154),
    overscan: 4,
  });

  const copyText = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("leads.copied", { label }));
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(t("leads.copied", { label }));
    }
  }, [t]);

  const closeDropdowns = () => {
    setShowCategoryModal(false);
    setCategorySearch("");
    setShowSortMenu(false);
  };

  const moreLeadIds = moreLead ? resolveWalmartIds(moreLead, currentRetailer) : null;
  const moreLeadOptions = moreLead ? getLeadVariantOptions(moreLead, currentRetailer) : [];
  const moreLeadLink = moreLead
    ? getLeadExternalLink(
        currentRetailer,
        currentRetailer === "walmart" || currentRetailer === "walmart_online"
          ? { id: moreLeadIds?.sku ?? "", sku: moreLeadIds?.sku ?? "" }
          : moreLead
      )
    : null;
  const openMoreLeadLink = useCallback(async () => {
    if (!moreLead || !moreLeadLink || !moreLeadIds) return;
    const usesAffiliate = ["walmart", "walmart_online", "target", "target_online"].includes(currentRetailer);
    const lead = moreLead;
    setMoreLead(null);
    if (!usesAffiliate) {
      window.open(moreLeadLink.url, "_blank", "noopener,noreferrer");
      return;
    }
    const tab = window.open("about:blank", "_blank");
    setMoreLinkLoading(true);
    try {
      const trackingUrl = await getAffiliateLink(moreLeadIds.sku || moreLeadIds.upc, moreLeadLink.url, currentRetailer);
      if (tab) tab.location.href = trackingUrl;
      else window.location.href = trackingUrl;
    } catch {
      if (tab) tab.location.href = moreLeadLink.url;
      else window.location.href = moreLeadLink.url;
    } finally {
      setMoreLinkLoading(false);
      void lead;
    }
  }, [currentRetailer, moreLead, moreLeadIds, moreLeadLink]);
  const shareMoreLead = useCallback(async () => {
    if (!moreLead || !moreLeadLink) return;
    const title = moreLead.title;
    const url = moreLeadLink.url;
    setMoreLead(null);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text: title, url });
        return;
      } catch {
        // fall through to copying the link
      }
    }
    await copyText(`${title}\n${url}`, t("leads.link"));
  }, [copyText, moreLead, moreLeadLink, t]);

  const performNearbySearch = useCallback(async (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }, zipCode: string) => {
    setShowNearbyResultsModal(true);
    setIsSearchingNearby(true);
    setNearbySearchError(null);
    setNearbySearchResults([]);
    setNearbyHomeDepotResults([]);
    setNearbyAmazonData(null);

    try {
      if (currentRetailer === "home_depot") {
        const response = await searchHomeDepotProduct(params.searchCode.trim(), zipCode);
        setNearbyHomeDepotResults(response.results);
        if (response.amazonPrice != null && response.amazonPrice > 0) {
          setNearbyAmazonData({
            price: response.amazonPrice,
            title: response.amazonTitle,
            url: response.amazonURL,
            isPrime: response.amazonIsPrime,
          });
        }
        if (response.results.length === 0) {
          setNearbySearchError(t("products.noResultsForProduct"));
        }
        return;
      }

      try {
        const response = await searchProduct(
          params.searchCode.trim(),
          params.searchType,
          zipCode,
          currentRetailer as
            | "walmart"
            | "home_depot"
            | "target"
            | "dollar_general"
            | "costco"
            | "bestbuy"
            | "lowes"
            | "tractor_supply"
        );
        setNearbySearchResults(response.results);
        if (response.amazonPrice != null && response.amazonPrice > 0) {
          setNearbyAmazonData({
            price: response.amazonPrice,
            title: response.amazonTitle,
            url: response.amazonURL,
            isPrime: response.amazonIsPrime,
          });
        }
        if (response.results.length === 0) {
          setNearbySearchError(t("products.noResultsForProduct"));
        }
      } catch (error) {
        if (!params.fallbackCode || !params.fallbackType) throw error;
        const fallbackResponse = await searchProduct(
          params.fallbackCode.trim(),
          params.fallbackType,
          zipCode,
          currentRetailer as
            | "walmart"
            | "home_depot"
            | "target"
            | "dollar_general"
            | "costco"
            | "bestbuy"
            | "lowes"
            | "tractor_supply"
        );
        setNearbySearchResults(fallbackResponse.results);
        if (fallbackResponse.amazonPrice != null && fallbackResponse.amazonPrice > 0) {
          setNearbyAmazonData({
            price: fallbackResponse.amazonPrice,
            title: fallbackResponse.amazonTitle,
            url: fallbackResponse.amazonURL,
            isPrime: fallbackResponse.amazonIsPrime,
          });
        }
        if (fallbackResponse.results.length === 0) {
          setNearbySearchError(t("products.noResultsForProduct"));
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t("leads.failedSearchProduct");
      setNearbySearchError(message);
    } finally {
      setIsSearchingNearby(false);
    }
  }, [currentRetailer, t]);

  const handleCheckStockSearch = useCallback(async (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => {
    const zipCode = user?.zipCode?.trim();
    if (!zipCode) {
      setPendingNearbySearch(params);
      setShowZipCodeModal(true);
      return;
    }
    await performNearbySearch(params, zipCode);
  }, [performNearbySearch, user?.zipCode]);

  const handleSeeAllFromTop = useCallback(() => setViewMode("all"), []);
  const handleTopSelectCategory = useCallback((category: string) => {
    setSelectedCategories(isProductCategory(category) ? [category] : []);
    setViewMode("all");
  }, []);

  const isTopLeadsMode = viewMode === "top" && totalLeadsCount > 0;
  const activeTab: LeadsViewMode = isTopLeadsMode ? "top" : "all";

  const leadsExplainer = (
    <aside className="leads-explainer" aria-label={t("leads.aboutTitle")}>
      <Sparkles size={16} className="leads-explainer-icon" aria-hidden="true" />
      <p className="leads-explainer-copy">
        <strong>{t("leads.aboutTitle")}</strong> <span>{t("leads.aboutBody", { action: t("leads.checkNearby") })}</span>
      </p>
      <button type="button" aria-label={t("leads.aboutDismiss")} onClick={dismissLeadsExplainer}>
        <X size={16} />
      </button>
    </aside>
  );

  if (isLoading) {
    return (
      <div className="leads-page flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-dark-950 min-h-0">
        <div className="flex-shrink-0 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 py-3 px-3 space-y-2.5">
          <div className="h-9 bg-gray-100 dark:bg-dark-700 rounded-lg animate-pulse" />
          <div className="flex gap-1.5">
            <div className="h-7 w-24 bg-gray-100 dark:bg-dark-700 rounded-full animate-pulse" />
            <div className="h-7 w-20 bg-gray-100 dark:bg-dark-700 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="flex-1 p-2 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-lg border border-gray-100 dark:border-dark-700 p-3 animate-pulse">
              <div className="flex gap-2.5">
                <div className="w-20 h-20 bg-gray-200 dark:bg-dark-700 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-1/2" />
                  <div className="h-5 bg-gray-200 dark:bg-dark-700 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 dark:bg-dark-950">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("products.errorLoadingLeads")}</h2>
          <p className="text-sm text-gray-500 dark:text-dark-400">{error instanceof Error ? error.message : t("leads.failedToLoad")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="leads-page flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-dark-950 min-h-0">
        {isFreeUser && (
          <div className="flex-shrink-0 px-2 pt-2">
            <UpgradeBanner
              message={t("products.freeLeadsBanner")}
              subMessage={t("products.freeLeadsBannerSub")}
              ctaLabel={t("products.freeLeadsUnlock")}
              onUpgrade={() => navigate("/settings?tab=billing&upgradeSource=leads_banner")}
            />
          </div>
        )}

        <div
          ref={toolbarRef}
          className={`leads-toolbar${toolbarHidden ? " is-hidden" : ""}`}
          style={toolbarHidden && toolbarHeight ? { marginTop: -toolbarHeight } : undefined}
        >
          {isFreeUser && !isTopLeadsMode && <div className="leads-preview-hint">{t("products.freeLeadsPreviewHint")}</div>}
          <div className="leads-toolbar-row">
            <LeadsViewToggle
              value={activeTab}
              onChange={handleTabChange}
              topCount={topLeadsCount}
              allCount={totalLeadsCount}
            />
            <div className="leads-chip-row-end">
              <LayoutModeMenu
                mode={isTopLeadsMode ? leadsTopLayout : leadsLayout}
                onChange={isTopLeadsMode ? setLeadsTopLayout : setLeadsLayout}
              />
              {!isTopLeadsMode && (
                <button
                  className="leads-icon-chip"
                  aria-label={isSearchOpen ? t("leads.hideSearch") : t("leads.showSearch")}
                  aria-pressed={isSearchOpen || searchQuery !== ""}
                  onClick={() => {
                    if (isSearchOpen) { setIsSearchOpen(false); setSearchQuery(""); }
                    else setIsSearchOpen(true);
                  }}
                >
                  {isSearchOpen ? <X size={17} /> : <Search size={17} />}
                </button>
              )}
              {isTopLeadsMode && (
                <button className="leads-icon-chip" aria-label={t("leads.refreshLeads")} disabled={isFetching} onClick={() => void refetch()}>
                  <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
                </button>
              )}
              {isAdmin && supportsDiscordImport(currentRetailer) && (
                <button aria-label={t("leads.createCategory")} title={t("leads.createLeadCategory")} className="leads-icon-chip leads-create-category" onClick={() => setShowImport(true)}>
                  <Plus size={17} />
                </button>
              )}
            </div>
          </div>
          {!isTopLeadsMode && (
            <>
              {(isSearchOpen || searchQuery !== "") && (
                <div className="leads-toolbar-row">
                  <div className="leads-search">
                    <Search size={16} />
                    <input
                      type="search"
                      aria-label={t("leads.searchLeads")}
                      placeholder={t("products.searchLeads")}
                      value={searchQuery}
                      autoFocus
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && <button aria-label={t("leads.clearSearch")} onClick={() => setSearchQuery("")}><X size={16} /></button>}
                  </div>
                </div>
              )}
              <div className="leads-toolbar-row leads-chip-row">
                <button
                  className="leads-chip leads-category-filter"
                  aria-label={t("leads.chooseCategory")}
                  aria-expanded={showCategoryModal}
                  aria-pressed={selectedCategories.length > 0}
                  onClick={() => { setShowSortMenu(false); setShowCategoryModal((open) => !open); }}
                >
                  <LayoutGrid size={14} /><span>{categoryPillLabel}</span><ChevronDown size={13} className="leads-chip-chevron" />
                </button>
                {!isFreeUser && (
                  <button
                    className="leads-chip leads-saved-toggle"
                    aria-label={t("leads.savedLeads")}
                    title={t("leads.savedLeads")}
                    aria-pressed={showSavedOnly}
                    onClick={() => setShowSavedOnly((on) => !on)}
                  >
                    <Heart size={15} fill={showSavedOnly ? "currentColor" : "none"} />
                    {showSavedOnly && savedCountForRetailer > 0 && <span>{savedCountForRetailer}</span>}
                  </button>
                )}
                <button className="leads-chip" aria-label={t("leads.sortLeads")} aria-expanded={showSortMenu} onClick={() => { setShowCategoryModal(false); setShowSortMenu((open) => !open); }}>
                  <ArrowUpDown size={13} /><span>{sortBy === "discount" ? t("leads.biggestSavings") : t("leads.newest")}</span><ChevronDown size={13} className="leads-chip-chevron" />
                </button>
                <div className="leads-chip-row-end">
                  <button className="leads-refresh-link" aria-label={t("leads.refreshLeads")} disabled={isFetching} onClick={() => void refetch()}>
                    <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {showLeadsExplainer && !isTopLeadsMode && filteredLeads.length === 0 && leadsExplainer}

        {isTopLeadsMode ? (
          <div className="flex-1 min-h-0">
            <TopLeadsView
              leads={leadsData?.data ?? []}
              hiddenCategories={leadsData?.hiddenCategories}
              categoryLabels={leadsData?.categoryLabels}
              totalAllCount={totalLeadsCount}
              onSeeAll={handleSeeAllFromTop}
              onSelectCategory={handleTopSelectCategory}
              hoursWindow={TOP_LEADS_WINDOW_HOURS}
              retailer={currentRetailer}
              onCheckStockSearch={handleCheckStockSearch}
              header={showLeadsExplainer ? leadsExplainer : undefined}
              layoutMode={leadsTopLayout}
            />
          </div>
        ) : (
        <>
        <span role="status" className="sr-only">{t("leads.leadsCount", { count: filteredLeads.length.toLocaleString() })}</span>

        {/* List */}
        {filteredLeads.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-gray-400 dark:text-dark-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("products.noLeadsFound")}</h3>
              <p className="text-sm text-gray-500 dark:text-dark-400">{t("products.noLeadsFoundHint")}</p>
            </div>
          </div>
        ) : (
          <div ref={parentRef} onScroll={handleListScroll} className="min-h-0 flex-1 w-full overflow-auto scrollbar-modern touch-auto overscroll-contain">
            {showLeadsExplainer && leadsExplainer}
            <div style={{ height: `${virtualizer.getTotalSize() + 150}px`, width: "100%", position: "relative" }}>
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const row = listRows[virtualItem.index];
                if (!row) return null;
                return (
                  <div
                    key={row.type === "header" ? `header-${row.key}-${virtualItem.index}` : `${row.leads[0].id}-${virtualItem.index}`}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualItem.start}px)` }}
                  >
                    {row.type === "header" ? (
                      <div className="lead-group-header" role="heading" aria-level={2}>
                        <span>{t(`leads.${row.key}`)}</span>
                        <span className="lead-group-count">{row.count.toLocaleString()}</span>
                      </div>
                    ) : (
                      <div
                        className={leadColumns > 1 ? "lead-grid-row" : isWideList
                          ? `lead-list-row${virtualItem.index === 0 ? " is-first" : ""}${virtualItem.index === listRows.length - 1 ? " is-last" : ""}`
                          : "lead-card-row"}
                        style={leadColumns > 1 ? { gridTemplateColumns: `repeat(${leadColumns}, minmax(0, 1fr))` } : undefined}
                      >
                        {row.leads.map((lead) => (
                          <LeadItem
                            key={lead.id}
                            lead={lead}
                            layout={leadColumns > 1 ? "tile" : isWideList ? "row" : "card"}
                            retailer={currentRetailer}
                            categoryLabels={leadsData?.categoryLabels}
                            onMore={setMoreLead}
                            disableFavorites={isFreeUser}
                            disableAlerts={isFreeUser}
                            onCheckStockSearch={handleCheckStockSearch}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Category multi-select sheet */}
      {showCategoryModal && createPortal(
        <div className="lead-sheet-backdrop sheet-backdrop-in" onClick={closeDropdowns}>
          <div className="lead-sheet lead-sheet-category sheet-panel-in-sm" role="dialog" aria-label={t("leads.chooseCategories")} onClick={(e) => e.stopPropagation()}>
            <div className="lead-sheet-handle" />
            <div className="lead-sheet-head">
              <h2 className="lead-sheet-title">{t("products.categoryLabel")}</h2>
              <div className="lead-sheet-head-actions">
                {selectedCategories.length > 0 && (
                  <button type="button" className="lead-sheet-link" aria-label={t("leads.clearCategories")} onClick={() => setSelectedCategories([])}>{t("leads.clear")}</button>
                )}
                {isAdmin && supportsDiscordImport(currentRetailer) && (
                  <button type="button" className="lead-sheet-link" onClick={() => { closeDropdowns(); setShowCategoryManager(true); }}>
                    <Settings2 size={13} /> {t("leads.manage")}
                  </button>
                )}
              </div>
            </div>
            {availableCategories.length > CATEGORY_SEARCH_THRESHOLD && (
              <div className="lead-sheet-search">
                <Search size={15} />
                <input
                  type="search"
                  aria-label={t("leads.findCategory")}
                  placeholder={t("leads.findCategory")}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
              </div>
            )}
            <div className="lead-sheet-scroll">
              {!categorySearch.trim() && (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={selectedCategories.length === 0}
                  className={`lead-cat-row${selectedCategories.length === 0 ? " is-selected" : ""}`}
                  onClick={() => setSelectedCategories([])}
                >
                  <span className={`lead-checkbox${selectedCategories.length === 0 ? " is-on" : ""}`} aria-hidden="true">{selectedCategories.length === 0 && <Check size={14} />}</span>
                  <span className="lead-cat-label">{t("products.allCategories")}</span>
                  <span className="lead-cat-count">{categoryCounts.get("all") || 0}</span>
                </button>
              )}
              {visibleCategories.map((cat) => {
                const isSelected = selectedCategories.includes(cat);
                const canManage = isAdmin && supportsDiscordImport(currentRetailer);
                return (
                  <div key={cat} className={`lead-cat-row${isSelected ? " is-selected" : ""}`}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      className="lead-cat-toggle"
                      onClick={() => toggleCategory(cat)}
                    >
                      <span className={`lead-checkbox${isSelected ? " is-on" : ""}`} aria-hidden="true">{isSelected && <Check size={14} />}</span>
                      <span className="lead-cat-label">{displayCategoryLabel(cat)}</span>
                      <span className="lead-cat-count">{categoryCounts.get(cat) || 0}</span>
                    </button>
                    {canManage && (
                      <button
                        type="button"
                        aria-label={t("leads.editLabelNamed", { name: displayCategoryLabel(cat) })}
                        title={t("leads.editLabel")}
                        disabled={deletingCategory !== null || editingCategory !== null}
                        onClick={() => void handleEditCategoryLabel(cat)}
                        className="lead-cat-admin"
                      >
                        {editingCategory === cat ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
                      </button>
                    )}
                    {canManage && (
                      <button
                        type="button"
                        aria-label={t("leads.deleteCategoryNamed", { name: displayCategoryLabel(cat) })}
                        title={t("leads.deleteCategory")}
                        disabled={deletingCategory !== null || editingCategory !== null}
                        onClick={() => void handleDeleteCategory(cat)}
                        className="lead-cat-admin lead-cat-admin-delete"
                      >
                        {deletingCategory === cat ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <button type="button" className="lead-sheet-done" aria-label={t("leads.applyCategories")} onClick={closeDropdowns}>
              {t("leads.showLeadsCount", { count: matchedLeads.length.toLocaleString() })}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Sort menu */}
      {showSortMenu && createPortal(
        <div className="lead-sheet-backdrop lead-sheet-backdrop-center sheet-backdrop-in" onClick={closeDropdowns}>
          <div className="lead-sheet lead-sheet-sort" role="dialog" aria-label={t("leads.sortLeads")} onClick={(e) => e.stopPropagation()}>
            <div className="lead-sheet-eyebrow">{t("leads.sortLeads")}</div>
            {([
              { key: "newest", label: t("leads.newestFirst") },
              { key: "discount", label: t("leads.biggestSavings") },
            ] as { key: LeadSort; label: string }[]).map((option) => (
              <button
                key={option.key}
                type="button"
                className={`lead-sort-row${sortBy === option.key ? " is-selected" : ""}`}
                aria-pressed={sortBy === option.key}
                onClick={() => { setSortBy(option.key); closeDropdowns(); }}
              >
                <span>{option.label}</span>
                {sortBy === option.key && <Check size={16} />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* More actions sheet */}
      {moreLead && moreLeadIds && moreLeadLink && createPortal(
        <div className="lead-sheet-backdrop sheet-backdrop-in" onClick={() => setMoreLead(null)}>
          <div className="lead-sheet lead-sheet-more sheet-panel-in-sm" role="dialog" aria-label={t("leads.moreActions")} onClick={(e) => e.stopPropagation()}>
            <div className="lead-sheet-handle" />
            <div className="lead-sheet-lead">
              <div className="lead-sheet-lead-image">
                {moreLead.image ? <img src={getProductImageUrl(moreLead.image, "150")} alt="" /> : <Box size={18} className="text-gray-400" />}
              </div>
              <p className="lead-sheet-lead-title">{moreLead.title}</p>
            </div>
            {moreLeadOptions.length > 0 && (
              <LeadOptions options={moreLeadOptions} onCheckStockSearch={(params) => { setMoreLead(null); void handleCheckStockSearch(params); }} />
            )}
            <button type="button" className="lead-sheet-row" onClick={() => void openMoreLeadLink()} disabled={moreLinkLoading}>
              <span className="lead-sheet-row-icon lead-sheet-row-icon-link">{moreLinkLoading ? <Loader2 size={17} className="animate-spin" /> : <ExternalLink size={17} />}</span>
              <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.visitSite", { site: moreLeadLink.label })}</span><span className="lead-sheet-row-sub">{t("leads.opensProductPage")}</span></span>
              <ChevronDown size={16} className="lead-sheet-row-chevron" />
            </button>
            {moreLeadIds.sku && (
              <button type="button" className="lead-sheet-row" onClick={() => { const value = moreLeadIds.sku; setMoreLead(null); void copyText(value, "SKU"); }}>
                <span className="lead-sheet-row-icon"><Copy size={17} /></span>
                <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.copySku")}</span><span className="lead-sheet-row-sub">{moreLeadIds.sku}</span></span>
                <ChevronDown size={16} className="lead-sheet-row-chevron" />
              </button>
            )}
            {moreLeadIds.upc && moreLeadIds.upc !== moreLeadIds.sku && (
              <button type="button" className="lead-sheet-row" onClick={() => { const value = moreLeadIds.upc; const label = getLeadIdLabel(currentRetailer); setMoreLead(null); void copyText(value, label); }}>
                <span className="lead-sheet-row-icon"><Copy size={17} /></span>
                <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.copyLabel", { label: getLeadIdLabel(currentRetailer) })}</span><span className="lead-sheet-row-sub">{moreLeadIds.upc}</span></span>
                <ChevronDown size={16} className="lead-sheet-row-chevron" />
              </button>
            )}
            <button type="button" className="lead-sheet-row" onClick={() => void shareMoreLead()}>
              <span className="lead-sheet-row-icon"><Share2 size={17} /></span>
              <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.share")}</span></span>
              <ChevronDown size={16} className="lead-sheet-row-chevron" />
            </button>
            {isAdmin && (
              <>
                <div className="lead-sheet-eyebrow lead-sheet-admin-eyebrow">{t("leads.admin")}</div>
                <button type="button" className="lead-sheet-row" aria-label={t("leads.adminEditLead")} onClick={() => { const lead = moreLead; setMoreLead(null); handleEditLead(lead); }}>
                  <span className="lead-sheet-row-icon"><Pencil size={17} /></span>
                  <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.adminEditLead")}</span></span>
                </button>
                {(currentRetailer === "walmart" || currentRetailer === "walmart_online") && (
                  <button type="button" className="lead-sheet-row" disabled={!!findingOptions[moreLead.id]} onClick={() => { const lead = moreLead; setMoreLead(null); void handleFindOptions(lead); }}>
                    <span className="lead-sheet-row-icon">{findingOptions[moreLead.id] ? <Loader2 size={17} className="animate-spin" /> : <Box size={17} />}</span>
                    <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.adminFindOptions")}</span></span>
                  </button>
                )}
                <button type="button" className="lead-sheet-row lead-sheet-row-danger" onClick={() => { const lead = moreLead; setMoreLead(null); void handleDeleteLead(lead); }}>
                  <span className="lead-sheet-row-icon"><Trash2 size={17} /></span>
                  <span className="lead-sheet-row-copy"><span className="lead-sheet-row-label">{t("leads.adminDeleteLead")}</span></span>
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {showImport && isAdmin && (
        <DiscordLeadImportModal
          key={currentRetailer}
          retailer={currentRetailer}
          onClose={() => setShowImport(false)}
          onViewCategory={category => {
            setSelectedCategories([leadCategoryKey(category)]);
            setSearchQuery("");
            setShowSavedOnly(false);
            setViewMode("all");
            setShowImport(false);
          }}
        />
      )}

      {showCategoryManager && isAdmin && (
        <ManageLeadCategoriesModal
          categories={leadsData?.manualCategories ?? []}
          categoryLabels={leadsData?.categoryLabels ?? {}}
          deletingCategory={deletingCategory}
          editingCategory={editingCategory}
          saving={savingCategoryOrder}
          onClose={() => setShowCategoryManager(false)}
          onCreate={() => { setShowCategoryManager(false); setShowImport(true); }}
          onDelete={(category) => void handleDeleteCategory(leadCategoryKey(category))}
          onEdit={(category) => void handleEditCategoryLabel(category)}
          onSave={(categories) => void handleSaveCategoryOrder(categories)}
        />
      )}

      {/* Edit Lead Modal */}
      {editingLead && <EditLeadModal lead={editingLead} onClose={() => setEditingLead(null)} onSave={handleSaveEdit} />}

      {showNearbyResultsModal && (
        <NearbyResults
          isSearching={isSearchingNearby}
          searchError={nearbySearchError}
          onClose={() => {
            setShowNearbyResultsModal(false);
            setNearbySearchResults([]);
            setNearbyHomeDepotResults([]);
            setNearbySearchError(null);
            setNearbyAmazonData(null);
          }}
          searchResults={nearbySearchResults}
          homeDepotSearchResults={nearbyHomeDepotResults}
          isHomeDepot={currentRetailer === "home_depot"}
          retailer={currentRetailer}
          title={t("products.storeAvailabilityLabel")}
          amazonData={nearbyAmazonData || undefined}
        />
      )}

      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => {
          setShowZipCodeModal(false);
          setPendingNearbySearch(null);
        }}
        onSuccess={async (zipCode) => {
          setShowZipCodeModal(false);
          if (pendingNearbySearch) {
            const nextSearch = pendingNearbySearch;
            setPendingNearbySearch(null);
            await performNearbySearch(nextSearch, zipCode);
          }
        }}
        currentZipCode={user?.zipCode || ""}
        title={t("products.setZipCodeTitle")}
        description={t("products.setZipCodeDesc")}
      />
    </>
  );
};

// Lead card: a horizontal card on phones, a photo tile in the desktop grid, a table-style row in the desktop list.
interface LeadItemProps {
  lead: Lead;
  layout: "card" | "tile" | "row";
  retailer: string;
  categoryLabels?: Record<string, string>;
  onMore: (lead: Lead) => void;
  onCheckStockSearch: (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => void | Promise<void>;
  disableFavorites?: boolean;
  disableAlerts?: boolean;
}

interface LeadOptionsProps {
  options: NonNullable<Lead["variantCriteria"]>;
  onCheckStockSearch: LeadItemProps["onCheckStockSearch"];
}

const LeadOptions: React.FC<LeadOptionsProps> = ({ options, onCheckStockSearch }) => {
  const { t } = useTranslation();
  return (
    <div className="lead-options">
      <div className="lead-options-head">
        <span>{t("products.productOptions")}</span>
        <span className="lead-options-hint">{t("leads.checkNearby")}</span>
      </div>
      <div className="lead-options-row">
        {options.map((option, optionIndex) => {
          const label = option.displayName || option.name || option.sku;
          const isUnavailable = option.availabilityStatus && option.availabilityStatus !== "AVAILABLE";
          return (
            <button
              key={`${option.sku}-${optionIndex}`}
              type="button"
              onClick={() => onCheckStockSearch({ searchCode: option.sku, searchType: "SKU" })}
              disabled={!option.sku}
              aria-label={`${t("products.nearbyButton")} ${label}`}
              title={isUnavailable ? `${label} · ${t("leads.unavailable")}` : label}
              className={`lead-option${isUnavailable ? " is-unavailable" : ""}`}
            >
              <span className="lead-option-thumb">
                <span>{label.slice(0, 1).toUpperCase()}</span>
                {option.image && (
                  <img src={getProductImageUrl(option.image, "80")} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                {!isUnavailable && <span className="lead-option-dot" />}
              </span>
              <span className="lead-option-label">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LeadItem = React.memo<LeadItemProps>(({ lead, layout, retailer, categoryLabels, onMore, onCheckStockSearch, disableFavorites, disableAlerts }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const { t } = useTranslation();
  const supportsAlerts = retailer === "walmart" || retailer === "home_depot" || retailer === "dollar_general";
  const resolved = resolveWalmartIds(lead, retailer);
  const stockSearchSku = getLeadStockSearchSku(lead, retailer);
  const optionCount = useMemo(() => getLeadVariantOptions(lead, retailer).length, [lead, retailer]);

  const originalPrice = getLeadOriginalPrice(lead.msrp, lead.onlinePrice);
  const discountPercent = getLeadDiscountPercent(originalPrice, lead.lowestPrice);
  const categoryKey = adjustedCategory(lead);
  const categoryLabel = categoryKey ? leadCategoryLabel(categoryKey, categoryLabels) : "";
  const metaText = [categoryLabel, formatRelativeTime(lead.lastUpdated, t)].filter(Boolean).join(" · ");

  const image = (
    <div className="lead-card-image">
      {lead.image ? <>
        {imageLoading && <div className="lead-image-loading"><ImageIcon size={22} /></div>}
        <img src={getProductImageUrl(lead.image, "300")} alt="" loading="lazy" className={imageLoading ? "opacity-0" : ""}
          onLoad={() => setImageLoading(false)} onError={() => setImageLoading(false)} />
      </> : <Box size={26} className="text-gray-400" />}
      {layout === "tile" && discountPercent > 0 && <span className="lead-tile-discount">-{discountPercent}%</span>}
      {layout === "tile" && !disableFavorites && <LeadSaveButton leadId={lead.id} retailer={retailer} compact className="lead-tile-save" />}
    </div>
  );
  const price = (
    <div className="lead-price-group">
      <span className="lead-current-price">{formatPrice(lead.lowestPrice)}</span>
      {originalPrice > lead.lowestPrice && <span className="lead-old-price">{formatPrice(originalPrice)}</span>}
    </div>
  );
  const optionsLink = optionCount > 0 && (
    <button type="button" className="lead-options-link" onClick={() => onMore(lead)}>
      <Layers size={13} />{t("leads.optionsCount", { count: optionCount + 1 })}
    </button>
  );
  const alertButton = supportsAlerts && !disableAlerts && (
    <SetAlertButton productUpc={resolved.upc} productSku={resolved.sku || resolved.upc}
      productName={lead.title} productImage={lead.image} productCategory={categoryKey} msrp={lead.msrp} currentPrice={lead.lowestPrice}
      className="lead-icon-action lead-alert-action" icon="bell" />
  );
  const moreButton = (
    <button type="button" className="lead-icon-action" aria-label={t("leads.moreActions")} onClick={() => onMore(lead)}>
      <MoreHorizontal size={18} />
    </button>
  );
  const nearbyButton = (
    <CheckStockButton productSku={stockSearchSku} productUpc={resolved.upc} onSearch={onCheckStockSearch} className="lead-nearby-action" label={t("leads.checkNearby")} />
  );

  if (layout === "row") {
    return (
      <article className="lead-list-item" aria-label={lead.title}>
        {image}
        <div className="lead-list-main">
          <h3 className="lead-product-title" title={lead.title}>{lead.title}</h3>
          <div className="lead-list-meta">
            {price}
            {discountPercent > 0 && <span className="lead-discount">{t("products.percentOff", { percent: String(discountPercent) })}</span>}
            <span className="lead-meta-text">{metaText}</span>
            {optionsLink}
          </div>
        </div>
        <div className="lead-list-actions">
          {alertButton}
          {!disableFavorites && <LeadSaveButton leadId={lead.id} retailer={retailer} compact className="lead-icon-action lead-save-action" />}
          {moreButton}
          {nearbyButton}
        </div>
      </article>
    );
  }

  if (layout === "tile") {
    return (
      <article className="lead-tile" aria-label={lead.title}>
        {image}
        <div className="lead-tile-body">
          {price}
          <h3 className="lead-product-title line-clamp-2" title={lead.title}>{lead.title}</h3>
          {/* Savings and options share one line, so no tile carries an empty row. */}
          <p className="lead-tile-line">
            {originalPrice > lead.lowestPrice && (
              <span className="lead-tile-save-amount">{t("products.saveAmount", { amount: formatPrice(originalPrice - lead.lowestPrice) })}</span>
            )}
            {originalPrice > lead.lowestPrice && optionCount > 0 && " · "}
            {optionCount > 0 && (
              <button type="button" className="lead-options-link" onClick={() => onMore(lead)}>{t("leads.optionsCount", { count: optionCount + 1 })}</button>
            )}
            {!(originalPrice > lead.lowestPrice) && optionCount === 0 && (lead.brand || "\u00a0")}
          </p>
          <p className="lead-tile-line">
            {categoryLabel && <span className="lead-tile-category">{categoryLabel}</span>}
            {categoryLabel && " · "}
            {formatRelativeTime(lead.lastUpdated, t)}
          </p>
          <div className="lead-tile-footer">
            {nearbyButton}
            {alertButton}
            {moreButton}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="lead-card" aria-label={lead.title}>
      {image}
      <div className="lead-card-body">
        <div className="lead-card-top">
          {discountPercent > 0 && <span className="lead-discount">{t("products.percentOff", { percent: String(discountPercent) })}</span>}
          <span className="lead-meta-text">{metaText}</span>
        </div>
        <h3 className="lead-product-title" title={lead.title}>{lead.title}</h3>
        {price}
        {optionsLink}
        <div className="lead-card-actions">
          {alertButton}
          {!disableFavorites && <LeadSaveButton leadId={lead.id} retailer={retailer} compact className="lead-icon-action lead-save-action" />}
          {moreButton}
          {nearbyButton}
        </div>
      </div>
    </article>
  );
});

interface EditLeadModalProps {
  lead: Lead;
  onClose: () => void;
  onSave: (data: UpdateLeadRequest) => void;
}

const EditLeadModal: React.FC<EditLeadModalProps> = ({ lead, onClose, onSave }) => {
  const [form, setForm] = useState<LeadEditFormState>({
    upc: lead.id, title: lead.title, sku: lead.sku, category: lead.category,
    lowestPrice: lead.lowestPrice, msrp: lead.msrp, onlinePrice: lead.onlinePrice, image: lead.image,
  });
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave(buildUpdateLeadRequest(form));
    setSaving(false);
  };

  const inputClass = "w-full px-2.5 py-1.5 text-sm bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-dark-300 mb-1";

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("products.editLead")}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-200"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div><label className={labelClass}>{t("leads.fieldTitle")}</label><input className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelClass}>{t("leads.fieldUpc")}</label><input className={inputClass} value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })} /></div>
            <div><label className={labelClass}>{t("leads.fieldSku")}</label><input className={inputClass} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
          </div>
          <div><label className={labelClass}>{t("leads.fieldCategory")}</label><input className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={labelClass}>{t("leads.fieldLowestPrice")}</label><input type="number" step="0.01" className={inputClass} value={form.lowestPrice} onChange={(e) => setForm({ ...form, lowestPrice: parseFloat(e.target.value) || 0 })} /></div>
            <div><label className={labelClass}>{t("leads.fieldMsrp")}</label><input type="number" step="0.01" className={inputClass} value={form.msrp} onChange={(e) => setForm({ ...form, msrp: parseFloat(e.target.value) || 0 })} /></div>
            <div><label className={labelClass}>{t("leads.fieldOnlinePrice")}</label><input type="number" step="0.01" className={inputClass} value={form.onlinePrice} onChange={(e) => setForm({ ...form, onlinePrice: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <div><label className={labelClass}>{t("leads.fieldImageUrl")}</label><input className={inputClass} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">{saving ? t("products.savingLabel") : t("common.save")}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default LeadsPage;
